import React, { useEffect, useState } from 'react';
import { sb } from '../../supabase';

// ⭐ on a product page, for admins only (catalog ordering step B, owner
// 2026-10-03). Featured products open the catalog (group 0 of
// browse_products), at most 25 at once (admin_settings.catalog_featured_cap),
// optionally until an end date — after it the product drops back on its own.
//
// Writes go through admin_feature_products / admin_unfeature_products, which
// check is_admin_user() server-side; the role test here only decides whether
// the control is drawn. Over the cap the RPC refuses and names the slots
// left — shown as is.
const COPY = {
  ar: { feature: '☆ تمييز المنتج', featured: '★ منتج مميّز', until: 'حتى', noEnd: 'بدون تاريخ انتهاء',
        remove: 'إلغاء التمييز', full: (n) => `لا توجد خانات كافية — المتبقي ${n} من 25.`, error: 'تعذّر الحفظ. حاول مرة أخرى.',
        endLabel: 'تاريخ الانتهاء (اختياري)' },
  en: { feature: '☆ Feature product', featured: '★ Featured', until: 'until', noEnd: 'no end date',
        remove: 'Unfeature', full: (n) => `Not enough slots — ${n} of 25 left.`, error: 'Could not save. Try again.',
        endLabel: 'End date (optional)' },
};

export const isAdminProfile = (profile) => ['admin', 'super_admin'].includes(String(profile?.role || '').toLowerCase());

function parseCapError(error) {
  const m = /catalog_featured_cap: (\d+) slots left/.exec(error?.message || '');
  return m ? Number(m[1]) : null;
}

export default function AdminFeatureStar({ profile, source, productId, lang = 'ar' }) {
  const c = COPY[lang === 'ar' ? 'ar' : 'en'];
  const isAdmin = isAdminProfile(profile);
  const [row, setRow] = useState(undefined);   // undefined = loading, null = not featured
  const [until, setUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!isAdmin || !productId) return undefined;
    let cancelled = false;
    (async () => {
      const { data, error } = await sb.from('catalog_featured')
        .select('featured_until').eq('source', source).eq('product_id', productId).maybeSingle();
      if (error) console.error('[AdminFeatureStar] read failed:', error.message);
      if (cancelled) return;
      const active = data && (!data.featured_until || new Date(data.featured_until) > new Date());
      setRow(active ? data : null);
    })();
    return () => { cancelled = true; };
  }, [isAdmin, source, productId]);

  if (!isAdmin || row === undefined) return null;

  const items = [{ source, product_id: productId }];
  const feature = async () => {
    setBusy(true); setMsg('');
    // End of the chosen day, Riyadh time.
    const endIso = until ? new Date(`${until}T23:59:59+03:00`).toISOString() : null;
    const { error } = await sb.rpc('admin_feature_products', { p_items: items, p_until: endIso });
    setBusy(false);
    if (error) {
      const left = parseCapError(error);
      setMsg(left != null ? c.full(left) : c.error);
      if (left == null) console.error('[AdminFeatureStar] feature failed:', error.message);
      return;
    }
    setRow({ featured_until: endIso });
  };
  const unfeature = async () => {
    setBusy(true); setMsg('');
    const { error } = await sb.rpc('admin_unfeature_products', { p_items: items });
    setBusy(false);
    if (error) { console.error('[AdminFeatureStar] unfeature failed:', error.message); setMsg(c.error); return; }
    setRow(null);
  };

  const box = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '0 0 14px',
    padding: '8px 10px', border: '1px dashed var(--border-strong, rgba(0,0,0,0.2))', borderRadius: 10,
    fontSize: 12.5, fontFamily: lang === 'ar' ? 'var(--font-ar)' : 'var(--font-sans)' };
  const btn = { padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-strong, rgba(0,0,0,0.2))',
    background: 'transparent', cursor: busy ? 'default' : 'pointer', fontSize: 12.5, fontFamily: 'inherit' };

  return (
    <div style={box} data-testid="admin-feature-star">
      {row ? (
        <>
          <span style={{ color: '#b7791f', fontWeight: 700 }}>{c.featured}</span>
          <span style={{ color: 'var(--text-secondary)' }}>
            {row.featured_until ? `${c.until} ${new Date(row.featured_until).toLocaleDateString(lang === 'ar' ? 'ar-SA-u-nu-latn' : 'en-GB')}` : c.noEnd}
          </span>
          <button type="button" style={btn} disabled={busy} onClick={unfeature}>{c.remove}</button>
        </>
      ) : (
        <>
          <button type="button" style={{ ...btn, fontWeight: 700 }} disabled={busy} onClick={feature}>{c.feature}</button>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)' }}>
            {c.endLabel}
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} style={{ fontSize: 12.5 }} />
          </label>
        </>
      )}
      {msg && <span role="status" style={{ color: '#b42318', flexBasis: '100%' }}>{msg}</span>}
    </div>
  );
}
